/**
 * Release flags for the web app: screens that are built but hidden for the first release.
 *
 * Every flag here is OFF by default. The code behind a flag stays in place; nav entries and tab
 * arrays filter on `isFeatureOn(...)`, and the hidden routes redirect home. To bring a screen back,
 * flip its value below. `VITE_SHOW_ALL=1` at build time turns every flag on (internal QA builds).
 *
 * These are build-time constants: nothing here runs on the API, so there is no per-request cost.
 */
export const FEATURE_FLAGS = {
  /** Integrations section (email/calendar sync, cloud storage, AI assistant, webhooks, API keys). */
  integrations: false,
  /** Project > Meetings (attendance, minutes, action items). */
  'project.meetings': false,
  /** Project > Bids (bid packages and leveling). */
  'project.bids': false,
  /** Projects > Bid invitations. */
  'bids.invitations': false,
  /** Financials > Accounting (QuickBooks / Sage sync). */
  'fin.accounting': false,
  /** Financials > Sub pay apps (progress billing from subs). */
  'fin.pay-apps': false,
  /** Financials > Lien waivers (a US practice; Canada uses holdback and statutory declarations). */
  'fin.lien-waivers': false,
  /** Subs & vendors: CSI divisions, prequalification and ratings (commercial GC qualification). */
  'crm.vendor-qualification': false,
  /** CRM reports beyond the three basics (stage conversion, forecast, cycle time, client value). */
  'crm.advanced-reports': false,
  /** SMS message templates (nothing sends SMS yet). */
  'crm.sms-templates': false,
} as const;

export type Feature = keyof typeof FEATURE_FLAGS;

function showAll(): boolean {
  try {
    const v = (import.meta as { env?: Record<string, unknown> }).env?.VITE_SHOW_ALL;
    return v === '1' || v === 'true';
  } catch {
    return false;
  }
}

const SHOW_ALL = showAll();

/** Whether a hidden-for-now feature is switched on in this build. */
export function isFeatureOn(feature: Feature): boolean {
  return SHOW_ALL || FEATURE_FLAGS[feature];
}

/** Keep the items whose `feature` (if any) is on. */
export function withFeatures<T extends { feature?: Feature }>(items: readonly T[]): T[] {
  return items.filter((i) => !i.feature || isFeatureOn(i.feature));
}

/** Business types that use the commercial document workflow (RFIs, submittals, drawings). */
export const COMMERCIAL_BUSINESS_TYPES = ['commercial', 'specialty_sub'] as const;

export function usesCommercialDocs(businessType: string | null | undefined): boolean {
  // Unknown business type: show everything rather than hide work someone may rely on.
  return !businessType || (COMMERCIAL_BUSINESS_TYPES as readonly string[]).includes(businessType);
}

/** Business types that use client selections (finishes, fixtures). */
export function usesSelections(businessType: string | null | undefined): boolean {
  return !businessType || businessType === 'residential' || businessType === 'remodeler';
}
